/** @format */

import React from "react";
import {
  useFetchApiRequestsCount,
  useQueryAccounts,
} from "../../../../apis/swr/fetchers";
import AsrChartsView from "./AsrCharts.view";
import 'chart.js/auto';
import { Chart as ChartJS, registerables } from 'chart.js';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

ChartJS.register(...registerables);

const colors = [
  '#2CAFFE', '#6B8ABC', '#00E272', '#FFFF99', '#FE6A35', 
  '#E6B333', '#3366E6', '#999966', '#99FF99', '#B34D4D',
  '#80B300', '#809900', '#E6B3B3', '#6680B3', '#66991A', 
  '#FF99E6', '#CCFF1A', '#FF1A66', '#E6331A', '#33FFCC',
  '#66994D', '#B366CC', '#4D8000', '#B33300', '#CC80CC', 
  '#66664D', '#991AFF', '#E666FF', '#4DB3FF', '#1AB399',
  '#E666B3', '#33991A', '#CC9999', '#B3B31A', '#00E680', 
  '#4D8066', '#809980', '#E6FF80', '#1AFF33', '#999933',
  '#FF3380', '#CCCC00', '#66E64D', '#4D80CC', '#9900B3', 
  '#E64D66', '#4DB380', '#FF4D4D', '#99E6E6', '#6666FF'
];

const AsrChartsContainer: React.FC = () => {
  const { data } = useFetchApiRequestsCount();
  const { accounts } = useQueryAccounts();

  const configureDate = (values: [string, number][]) => {
    return values.map((value) => {
      const date = new Date(value[0]);
      const utcDate = Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
      );
      return { x: utcDate, y: value[1] };
    });
  };

  const datasets = data
    ? data.map((account, index) => {
        const findWebname = (id: string) => {
          const found =
            accounts && accounts.find((account) => account.accountId === id);
          if (found) return found.webName;
          else return null;
        };
        if (
          account &&
          account.values &&
          account.values.length &&
          account.values.length > 0
        ) {
          return {
            label: account.accountId
              ? `${account.accountId}-${findWebname(account.accountId)}`
              : "No ID",
            data: configureDate(account.values),
            fill: false,
            borderColor: colors[index % colors.length],
            // tension: 0.4,
            pointHoverRadius: 10, // Increase hover radius
            pointHitRadius: 10,   // Increase hit radius
          };
        } else return undefined;
      }).filter(Boolean) // filter out any undefined values
    : [];

  const chartData = {
    datasets: datasets,
  };

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false, // this will allow us to control the height
  aspectRatio: 2, // this will set the height to half of its default
  plugins: {
    title: {
      display: true,
      text: 'ASR Web API Requests',
      position: 'top',
      font: {
        size: 20,
        family: 'Helvetica, Arial, sans-serif'
      },
      color: 'rgb(51, 51, 51)',
      padding: {
        top: 0,
        bottom: 20,
      },
    },
    tooltip: {
      position: 'nearest',
      backgroundColor: '#ffffff', // White background
      titleColor: '#000000', // Black title text
      bodyColor: '#000000', // Black body text
      titleAlign: 'center',
      bodyAlign: 'center',
      displayColors: false,
      borderColor: '#dddddd', // Border color for tooltip
      borderWidth: 1, // Border width for tooltip
    },
    legend: {
      display: true,
      position: 'bottom', // Move legend to the bottom
      align: 'center', // Center align the legend
      onClick: (e, legendItem) => {
        const index = legendItem.datasetIndex;
        const ci = e.chart;
        const alreadyHidden = ci.getDatasetMeta(index).hidden === null ? false : ci.getDatasetMeta(index).hidden;

        ci.data.datasets.forEach((e, i) => {
          const meta = ci.getDatasetMeta(i);

          if (i !== index) {
            if (!alreadyHidden) {
              meta.hidden = meta.hidden === null ? !meta.hidden : null;
            } else if (meta.hidden === true) {
              meta.hidden = null;
            }
          } else if (i === index) {
            meta.hidden = null;
          }
        });

        ci.update();
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: 'day',
      },
      title: {
        display: true,
        text: 'Time',
      },
      grid: {
        display: false // hide vertical grid lines
      }
    },
    y: {
      title: {
        display: true,
        text: 'Count',
      },
      grid: {
        drawBorder: false, // hide grid line borders
        drawOnChartArea: true, // keep horizontal grid lines
        drawTicks: false, // hide ticks
        color: 'rgba(200, 200, 200, 0.2)' // light color for grid lines
      }
    },
  },
};

  const downloadChart = async (format) => {
    const chartCanvas = document.getElementById('chart-canvas');
    if (!chartCanvas) {
      console.error('Chart canvas not found.');
      return;
    }
    
    const canvas = await html2canvas(chartCanvas);
    const imgData = canvas.toDataURL('image/png');
    
    if (format === 'png') {
      saveAs(imgData, 'chart.png');
    } else if (format === 'jpg') {
      const imgDataJpg = canvas.toDataURL('image/jpeg');
      saveAs(imgDataJpg, 'chart.jpg');
    } else if (format === 'pdf') {
      const pdf = new jsPDF({
        orientation: 'landscape',
      });
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('chart.pdf');
    }
  };

  return (
    <AsrChartsView 
      chartData={chartData} 
      chartOptions={chartOptions} 
      data={data ? data : []} 
      downloadChart={downloadChart}
    />
  );
};

export default AsrChartsContainer;
